/**
 * AdminTemplates.js
 *
 * Admin subtab for managing signup/creation template presets.
 * Templates define the default featureFlags + flagMinRoles for new companies
 * and the default settings for new users.
 *
 * Data lives in globalSettings/templates with two top-level maps:
 *   - companyPresets  (keyed by preset slug)
 *   - userPresets     (keyed by preset slug)
 *
 * Document type presets live on a separate subtab (AdminDocumentTypeTemplates.js).
 *
 * Protected presets (isProtected: true) cannot be deleted but can still be
 * edited — the flag is a safety guardrail, not an absolute lock.
 *
 * See: docs/features/infrastructure/feature-access-matrix.md#settings-templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useUnsavedChanges } from './useUnsavedChanges';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Plus, Trash2, Star, Copy, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/authContext';
import {
  companyFlags,
  ROLE_HIERARCHY,
  ROLE_LABELS,
  defaultCompanyFeatureFlags,
  defaultCompanyFlagMinRoles,
} from '../config/featureFlagDefinitions';
import { DEFAULT_USER_SETTINGS } from '../config/userSettingsDefaults';

// Keys shown in the user-preset editor, grouped for readability.
// Only settings an admin would meaningfully want to configure for new users.
//
// Hidden from UI but kept where active consumers still need them:
//   transcriptType    — document formatter behavior pending migration
//   documentType      — last selected AppShell document type
//   microphone        — always "default"; user picks at runtime (remove from template)
//   audioProvider     — always "deepgram"; single provider (remove from template)
//
const USER_SETTING_GROUPS = [
  {
    label: 'Feature Preferences',
    keys: [
      { key: 'useDarkMode', label: 'Dark Mode', type: 'boolean' },
      { key: 'showTags', label: 'Show Tags', type: 'boolean' },
      { key: 'showSeverity', label: 'Show Severity', type: 'boolean' },
      { key: 'betaDocumentTypes', label: 'Beta Document Types', type: 'boolean' },
      { key: 'useAiAssistant', label: 'AI Assistant', type: 'boolean' },
    ],
  },
  {
    label: 'Defaults',
    keys: [
      { key: 'collapsedSections', label: 'Remember Collapsed Sections', type: 'boolean' },
    ],
  },
];

/** Generate a URL-safe slug from a label */
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const AdminTemplates = ({ onDirtyChange }) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Raw template data from Firestore
  const [companyPresets, setCompanyPresets] = useState({});
  const [userPresets, setUserPresets] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const currentSnapshot = JSON.stringify({ companyPresets, userPresets });
  const hasUnsavedChanges = !isLoading && savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  useUnsavedChanges(hasUnsavedChanges);
  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Which preset is expanded for editing
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  // New preset dialog
  const [newPresetDialog, setNewPresetDialog] = useState({ open: false, type: null });
  const [newPresetLabel, setNewPresetLabel] = useState('');

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, key: null, label: '' });

  // ------------------------------------------
  // Load
  // ------------------------------------------
  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'globalSettings', 'templates'));
      if (snap.exists()) {
        const data = snap.data();
        const loadedCompanyPresets = data.companyPresets || {};
        const loadedUserPresets = data.userPresets || {};
        setCompanyPresets(loadedCompanyPresets);
        setUserPresets(loadedUserPresets);
        setSavedSnapshot(JSON.stringify({ companyPresets: loadedCompanyPresets, userPresets: loadedUserPresets }));
      } else {
        // No templates doc yet — start with empty
        setCompanyPresets({});
        setUserPresets({});
        setSavedSnapshot(JSON.stringify({ companyPresets: {}, userPresets: {} }));
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ------------------------------------------
  // Save
  // ------------------------------------------
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const activeCompanyPresets = Object.fromEntries(Object.entries(companyPresets).map(([key, preset]) => {
        const featureFlags = Object.fromEntries(
          Object.entries(preset.featureFlags || {}).filter(([flag]) => Object.prototype.hasOwnProperty.call(defaultCompanyFeatureFlags, flag)),
        );
        const flagMinRoles = Object.fromEntries(
          Object.entries(preset.flagMinRoles || {}).filter(([flag]) => Object.prototype.hasOwnProperty.call(defaultCompanyFeatureFlags, flag)),
        );
        return [key, { ...preset, featureFlags, flagMinRoles }];
      }));
      const activeUserPresets = Object.fromEntries(Object.entries(userPresets).map(([key, preset]) => {
        const settings = Object.fromEntries(
          Object.entries(preset.settings || {}).filter(([setting]) => Object.prototype.hasOwnProperty.call(DEFAULT_USER_SETTINGS, setting)),
        );
        return [key, { ...preset, settings }];
      }));
      await setDoc(
        doc(db, 'globalSettings', 'templates'),
        {
          companyPresets: activeCompanyPresets,
          userPresets: activeUserPresets,
          updatedAt: new Date(),
          updatedBy: currentUser?.email || 'unknown',
        },
        { merge: true },
      );
      setSavedSnapshot(currentSnapshot);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving templates:', err);
      setError('Failed to save templates');
    }
    setIsSaving(false);
  };

  // ------------------------------------------
  // Company preset helpers
  // ------------------------------------------
  const updateCompanyPreset = (presetKey, path, value) => {
    setCompanyPresets((prev) => {
      const preset = { ...prev[presetKey] };
      if (path.length === 1) {
        preset[path[0]] = value;
      } else if (path.length === 2) {
        preset[path[0]] = { ...preset[path[0]], [path[1]]: value };
      }
      return { ...prev, [presetKey]: preset };
    });
  };

  const setDefaultCompanyPreset = (presetKey) => {
    setCompanyPresets((prev) => {
      const updated = {};
      for (const [k, v] of Object.entries(prev)) {
        updated[k] = { ...v, isDefault: k === presetKey };
      }
      return updated;
    });
  };

  const duplicateCompanyPreset = (sourceKey) => {
    const source = companyPresets[sourceKey];
    const newLabel = `${source.label} (Copy)`;
    const newKey = slugify(newLabel) || `copy-${Date.now()}`;
    setCompanyPresets((prev) => ({
      ...prev,
      [newKey]: {
        ...JSON.parse(JSON.stringify(source)),
        label: newLabel,
        isDefault: false,
        isProtected: false,
      },
    }));
    setExpandedCompany(newKey);
  };

  const deleteCompanyPreset = (presetKey) => {
    setCompanyPresets((prev) => {
      const { [presetKey]: _, ...rest } = prev;
      return rest;
    });
    if (expandedCompany === presetKey) setExpandedCompany(null);
    setDeleteDialog({ open: false, type: null, key: null, label: '' });
  };

  // ------------------------------------------
  // User preset helpers
  // ------------------------------------------
  const updateUserPreset = (presetKey, path, value) => {
    setUserPresets((prev) => {
      const preset = { ...prev[presetKey] };
      if (path.length === 1) {
        preset[path[0]] = value;
      } else if (path.length === 2) {
        preset[path[0]] = { ...preset[path[0]], [path[1]]: value };
      }
      return { ...prev, [presetKey]: preset };
    });
  };

  const setDefaultUserPreset = (presetKey) => {
    setUserPresets((prev) => {
      const updated = {};
      for (const [k, v] of Object.entries(prev)) {
        updated[k] = { ...v, isDefault: k === presetKey };
      }
      return updated;
    });
  };

  const duplicateUserPreset = (sourceKey) => {
    const source = userPresets[sourceKey];
    const newLabel = `${source.label} (Copy)`;
    const newKey = slugify(newLabel) || `copy-${Date.now()}`;
    setUserPresets((prev) => ({
      ...prev,
      [newKey]: {
        ...JSON.parse(JSON.stringify(source)),
        label: newLabel,
        isDefault: false,
        isProtected: false,
      },
    }));
    setExpandedUser(newKey);
  };

  const deleteUserPreset = (presetKey) => {
    setUserPresets((prev) => {
      const { [presetKey]: _, ...rest } = prev;
      return rest;
    });
    if (expandedUser === presetKey) setExpandedUser(null);
    setDeleteDialog({ open: false, type: null, key: null, label: '' });
  };

  // ------------------------------------------
  // Create new preset
  // ------------------------------------------
  const handleCreatePreset = () => {
    const { type } = newPresetDialog;
    const label = newPresetLabel.trim();
    if (!label) return;
    const key = slugify(label) || `preset-${Date.now()}`;

    if (type === 'company') {
      setCompanyPresets((prev) => ({
        ...prev,
        [key]: {
          label,
          isDefault: Object.keys(prev).length === 0,
          isProtected: false,
          featureFlags: { ...defaultCompanyFeatureFlags },
          flagMinRoles: { ...defaultCompanyFlagMinRoles },
        },
      }));
      setExpandedCompany(key);
    } else {
      setUserPresets((prev) => ({
        ...prev,
        [key]: {
          label,
          isDefault: Object.keys(prev).length === 0,
          isProtected: false,
          settings: { ...DEFAULT_USER_SETTINGS },
        },
      }));
      setExpandedUser(key);
    }

    setNewPresetDialog({ open: false, type: null });
    setNewPresetLabel('');
  };

  // ------------------------------------------
  // Render helpers
  // ------------------------------------------

  /** Render a single company preset card */
  const renderCompanyPreset = (presetKey, preset) => {
    const isExpanded = expandedCompany === presetKey;
    const flags = preset.featureFlags || {};
    const minRoles = preset.flagMinRoles || {};

    return (
      <Card key={presetKey} variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setExpandedCompany(isExpanded ? null : presetKey)}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Typography variant="subtitle2">{preset.label}</Typography>
              {preset.isDefault && <Chip label="Default" size="small" color="primary" variant="outlined" />}
              {preset.isProtected && (
                <Tooltip title="Protected preset" arrow>
                  <Shield size={14} style={{ opacity: 0.5 }} />
                </Tooltip>
              )}
            </Box>
            <Tooltip title="Set as default" arrow>
              <IconButton size="small" onClick={() => setDefaultCompanyPreset(presetKey)} disabled={preset.isDefault}>
                <Star size={16} style={{ fill: preset.isDefault ? 'currentColor' : 'none' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate" arrow>
              <IconButton size="small" onClick={() => duplicateCompanyPreset(presetKey)}>
                <Copy size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={preset.isProtected ? 'Protected — cannot delete' : 'Delete'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={preset.isProtected}
                  onClick={() =>
                    setDeleteDialog({ open: true, type: 'company', key: presetKey, label: preset.label })
                  }
                >
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Expanded editor */}
          {isExpanded && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Label"
                size="small"
                fullWidth
                value={preset.label}
                onChange={(e) => updateCompanyPreset(presetKey, ['label'], e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Feature Flags
              </Typography>

              {companyFlags.map((flag) => (
                <Box key={flag.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FormControlLabel
                    sx={{ flex: 1, mr: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={flags[flag.key] ?? flag.companyDefault}
                        onChange={(e) => updateCompanyPreset(presetKey, ['featureFlags', flag.key], e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {flag.label}
                      </Typography>
                    }
                  />
                  {flag.minRole != null && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Min Role</InputLabel>
                      <Select
                        label="Min Role"
                        value={minRoles[flag.key] || ''}
                        onChange={(e) =>
                          updateCompanyPreset(presetKey, ['flagMinRoles', flag.key], e.target.value)
                        }
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {ROLE_HIERARCHY.map((role) => (
                          <MenuItem key={role} value={role} sx={{ fontSize: '0.8rem' }}>
                            {ROLE_LABELS[role]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  /** Render a single user preset card */
  const renderUserPreset = (presetKey, preset) => {
    const isExpanded = expandedUser === presetKey;
    const settings = preset.settings || {};

    return (
      <Card key={presetKey} variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setExpandedUser(isExpanded ? null : presetKey)}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Typography variant="subtitle2">{preset.label}</Typography>
              {preset.isDefault && <Chip label="Default" size="small" color="primary" variant="outlined" />}
              {preset.isProtected && (
                <Tooltip title="Protected preset" arrow>
                  <Shield size={14} style={{ opacity: 0.5 }} />
                </Tooltip>
              )}
            </Box>
            <Tooltip title="Set as default" arrow>
              <IconButton size="small" onClick={() => setDefaultUserPreset(presetKey)} disabled={preset.isDefault}>
                <Star size={16} style={{ fill: preset.isDefault ? 'currentColor' : 'none' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate" arrow>
              <IconButton size="small" onClick={() => duplicateUserPreset(presetKey)}>
                <Copy size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={preset.isProtected ? 'Protected — cannot delete' : 'Delete'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={preset.isProtected}
                  onClick={() =>
                    setDeleteDialog({ open: true, type: 'user', key: presetKey, label: preset.label })
                  }
                >
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Expanded editor */}
          {isExpanded && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Label"
                size="small"
                fullWidth
                value={preset.label}
                onChange={(e) => updateUserPreset(presetKey, ['label'], e.target.value)}
                sx={{ mb: 2 }}
              />

              {USER_SETTING_GROUPS.map((group) => (
                <React.Fragment key={group.label}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1, mt: 1.5 }}>
                    {group.label}
                  </Typography>
                  {group.keys.map(({ key, label, type, options }) => {
                    if (type === 'boolean') {
                      return (
                        <FormControlLabel
                          key={key}
                          sx={{ display: 'flex', mb: 0.25 }}
                          control={
                            <Switch
                              size="small"
                              checked={settings[key] ?? false}
                              onChange={(e) =>
                                updateUserPreset(presetKey, ['settings', key], e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {label}
                            </Typography>
                          }
                        />
                      );
                    }
                    if (type === 'select') {
                      return (
                        <FormControl key={key} size="small" sx={{ minWidth: 180, mb: 1, mr: 1 }}>
                          <InputLabel sx={{ fontSize: '0.75rem' }}>{label}</InputLabel>
                          <Select
                            label={label}
                            value={settings[key] ?? ''}
                            onChange={(e) =>
                              updateUserPreset(presetKey, ['settings', key], e.target.value)
                            }
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {options.map((opt) => (
                              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }
                    if (type === 'number') {
                      return (
                        <TextField
                          key={key}
                          label={label}
                          type="number"
                          size="small"
                          value={settings[key] ?? 0}
                          onChange={(e) =>
                            updateUserPreset(presetKey, ['settings', key], parseInt(e.target.value) || 0)
                          }
                          sx={{ minWidth: 180, mb: 1, mr: 1 }}
                        />
                      );
                    }
                    return null;
                  })}
                </React.Fragment>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Templates saved successfully.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Template presets define the initial feature flags and settings applied when creating
        new companies or users. The preset marked as "Default" is used automatically.
      </Typography>

      {/* ---- Company Presets ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1 }}>
        <Typography variant="h6">Company Presets</Typography>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setNewPresetDialog({ open: true, type: 'company' })}
        >
          Add Preset
        </Button>
      </Box>

      {Object.keys(companyPresets).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No company presets configured. Code defaults will be used.
        </Typography>
      ) : (
        Object.entries(companyPresets).map(([key, preset]) => renderCompanyPreset(key, preset))
      )}

      <Divider sx={{ my: 3 }} />

      {/* ---- User Presets ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">User Presets</Typography>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setNewPresetDialog({ open: true, type: 'user' })}
        >
          Add Preset
        </Button>
      </Box>

      {Object.keys(userPresets).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No user presets configured. Code defaults will be used.
        </Typography>
      ) : (
        Object.entries(userPresets).map(([key, preset]) => renderUserPreset(key, preset))
      )}

      {/* Save button */}
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Templates'}
        </Button>
      </Box>

      {/* ---- New Preset Dialog ---- */}
      <Dialog
        open={newPresetDialog.open}
        onClose={() => {
          setNewPresetDialog({ open: false, type: null });
          setNewPresetLabel('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          New {newPresetDialog.type === 'company' ? 'Company' : 'User'} Preset
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Preset Name"
            fullWidth
            size="small"
            value={newPresetLabel}
            onChange={(e) => setNewPresetLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreatePreset()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewPresetDialog({ open: false, type: null });
              setNewPresetLabel('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreatePreset} disabled={!newPresetLabel.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete Confirmation Dialog ---- */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, key: null, label: '' })}
        maxWidth="xs"
      >
        <DialogTitle>Delete Preset</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the "{deleteDialog.label}" preset? This cannot be undone
            after saving.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, key: null, label: '' })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteDialog.type === 'company') {
                deleteCompanyPreset(deleteDialog.key);
              } else {
                deleteUserPreset(deleteDialog.key);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTemplates;
